import React, { useState, useEffect } from "react";
import api from "../services/api"; 
import { getDefaultTheme } from "../constants/themeDefaults";
import "./mariage.css";

interface ThemeData {
  background: string;
  heart: string;
  text: string;
  accent: string;
  overlay: string;
  box: string;
  accentLine: string;
  inviteBoxBackground: string;
  sectionsBackground: string;
}

interface FormDataState {
  template: string;
  bride: string;
  groom: string;
  province: string;
  photoBackground: File | null;
  invitationImage: File | null;
  jourSemaine: string;
  dateISO: string;
  heureISO: string;
  tableName: string;
  numeroMarie: string;
  numeroPicApic: string;
  mapEmbedUrl: string;
  theme: ThemeData;
  boissonsSansAlcool: string[];
  boissonsAvecAlcool: string[];
  photoBackgroundUrl: string;
  invitationImageUrl: string;
  
  familleMarie: string;
  familleMariee: string;
  lieuReception: string;
  adresseTexte: string;
  citation: string;
  referenceCitation: string;
  programme1: string;
  programme2: string;
  
  posteMarie: string;
  posteMariee: string;
  phraseUnion: string;
  
  livreOrActive: boolean;
}

interface ProvincesResponse {
  success: boolean;
  provinces: string[];
}

interface MariageResponse {
  success: boolean;
  id: string;
  message?: string;
}

const themeColors: Record<string, { colors: string[], family: string }> = {
  ClassiqueHero: { 
    colors: ["#121212", "#c2a46d", "#e8e6e3"], 
    family: "CLASSIQUE" 
  },
  BeigeMarron: { 
    colors: ["#3b312c", "#cbb7a1", "#f0ebe5"], 
    family: "CLASSIQUE" 
  },
  CarminRose: { 
    colors: ["#4a1f28", "#d6b0a0", "#f3ece8"], 
    family: "CLASSIQUE" 
  },
  
  ModerneHero: { 
    colors: ["#f5f1ea", "#bfa37a", "#3a3a3a"], 
    family: "MODERNE" 
  },
  VertForet: { 
    colors: ["#1f2a38", "#c7cbd6", "#e6e9ef"], 
    family: "MODERNE" 
  },
  BleuNuit: { 
    colors: ["#0f1c2b", "#d2c2a8", "#e9e6e1"], 
    family: "MODERNE" 
  },
  
  ElegantHero: { 
    colors: ["#002c3c", "#ff4d6d", "#c49f59"], 
    family: "ÉLÉGANT" 
  },
  MauveSaphir: { 
    colors: ["#2b2430", "#c8b6c8", "#e7e3ea"], 
    family: "ÉLÉGANT" 
  },
  BordeauxPeche: { 
    colors: ["#3a1f24", "#d4b8a3", "#f1eae4"], 
    family: "ÉLÉGANT" 
  },
  RoseBordeaux: { 
    colors: ["#3c2529", "#d8b4b4", "#f2ecec"], 
    family: "ÉLÉGANT" 
  },
  
  TempleHero: { 
    colors: ["#2a3a4a", "#c9b8a8", "#f0e8e0"], 
    family: "TEMPLE" 
  },
  TempleNoir: { 
    colors: ["#2a2a2a", "#b8a898", "#f0e8e0"], 
    family: "TEMPLE" 
  },
  TempleSable: { 
    colors: ["#3a3a3a", "#d4b8a8", "#f0e8e0"], 
    family: "TEMPLE" 
  },
};

const getThemeDisplayName = (themeKey: string): string => {
  const displayNames: Record<string, string> = {
    ClassiqueHero: "Noir Doux & Or Mat",
    BeigeMarron: "Beige & Brun Doux",
    CarminRose: "Rouge Profond Doux",
    
    ModerneHero: "Champagne Clair",
    VertForet: "Bleu Roi Calme",
    BleuNuit: "Bleu Nuit Doux",
    
    ElegantHero: "Élégant Hero",
    MauveSaphir: "Mauve Poudré Doux",
    BordeauxPeche: "Bordeaux Élégant",
    RoseBordeaux: "Rose Pâle Calme",
    
    TempleHero: "Temple Ardoise",
    TempleNoir: "Temple Graphite",
    TempleSable: "Temple Plomb"
  };
  return displayNames[themeKey] || themeKey;
};

const ColorInput = ({ label, name, value, onChange }: { 
  label: string; 
  name: string; 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const isRgba = value.startsWith('rgba');
  
  return (
    <div className="form-group">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {!isRgba && (
          <input 
            type="color" 
            name={name} 
            value={value} 
            onChange={onChange} 
            style={{ width: '50px', height: '40px', cursor: 'pointer' }}
          />
        )}
        <input 
          type="text" 
          name={name} 
          value={value} 
          onChange={onChange} 
          style={{ flex: 1, padding: '8px' }}
        />
        <div 
          style={{ 
            width: '40px', 
            height: '40px', 
            backgroundColor: value,
            border: '2px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }} 
          title={value}
        />
      </div>
      {isRgba && (
        <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
          💡 Couleur avec transparence (modifiable dans le champ texte)
        </small>
      )}
    </div>
  );
};

const MariageForm: React.FC = () => {
  const [provinces, setProvinces] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormDataState>({
    template: "ElegantHero",
    bride: "",
    groom: "",
    province: "",
    photoBackground: null,
    invitationImage: null,
    jourSemaine: "",
    dateISO: "",
    heureISO: "",
    tableName: "",
    numeroMarie: "+243",
    numeroPicApic: "+243",
    mapEmbedUrl: "",
    theme: getDefaultTheme("ElegantHero"),
    boissonsSansAlcool: [],
    boissonsAvecAlcool: [],
    photoBackgroundUrl: "",
    invitationImageUrl: "",
    
    familleMarie: "",
    familleMariee: "",
    lieuReception: "",
    adresseTexte: "",
    citation: "",
    referenceCitation: "",
    programme1: "",
    programme2: "",
    
    posteMarie: "",
    posteMariee: "",
    phraseUnion: "",
    
    livreOrActive: true
  });

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await api.get<ProvincesResponse>("/admin/users/provinces");
      if (response.data.success) {
        setProvinces(response.data.provinces);
      }
    } catch (error) {
      console.error("Erreur chargement provinces:", error);
    }
  };

  const getJourSemaine = (dateString: string): string => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const jours = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    return jours[date.getDay()];
  };

  const formatDateForDisplay = (dateISO: string): string => {
    if (!dateISO) return "";
    const date = new Date(dateISO);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTimeForDisplay = (heureISO: string): string => {
    if (!heureISO) return "";
    const [hours, minutes] = heureISO.split(':');
    return `${hours}h${minutes}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const target = e.target as HTMLInputElement;
    const isCheckbox = target.type === 'checkbox';
    const checked = target.checked;
    
    if (name === "template") {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        theme: getDefaultTheme(value)
      }));
    } else if (name === "dateISO") {
      const jourSemaine = getJourSemaine(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        jourSemaine: jourSemaine
      }));
    } else if (isCheckbox) {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [name]: value
      }
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const fileUrl = URL.createObjectURL(file);
      setFormData(prev => ({ 
        ...prev, 
        [name]: file,
        [`${name}Url`]: fileUrl
      }));
    }
  };

  const handleAddBoisson = (type: "boissonsSansAlcool" | "boissonsAvecAlcool") => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], ""]
    }));
  };

  const handleBoissonChange = (
    type: "boissonsSansAlcool" | "boissonsAvecAlcool",
    index: number,
    value: string
  ) => {
    setFormData(prev => {
      const newList = [...prev[type]];
      newList[index] = value;
      return { ...prev, [type]: newList };
    });
  };

  const handleRemoveBoisson = (type: "boissonsSansAlcool" | "boissonsAvecAlcool", index: number) => {
    setFormData(prev => {
      const newList = [...prev[type]];
      newList.splice(index, 1);
      return { ...prev, [type]: newList };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.bride || !formData.groom || !formData.dateISO || !formData.heureISO || !formData.province) {
      alert("Veuillez remplir tous les champs obligatoires : mariée, marié, date, heure et province");
      return;
    }

    const congoleseRegex = /^\+243[0-9]{9}$/;
    if (!congoleseRegex.test(formData.numeroMarie)) {
      alert("Le numéro du marié doit être au format congolais : +243 suivi de 9 chiffres");
      return;
    }
    
    if (!congoleseRegex.test(formData.numeroPicApic)) {
      alert("Le numéro PicApic doit être au format congolais : +243 suivi de 9 chiffres");
      return;
    }

    const formDataToSend = new FormData();

    const simpleFields = [
      'template', 'bride', 'groom', 'province', 'jourSemaine', 'dateISO', 
      'heureISO', 'tableName', 'numeroMarie', 'numeroPicApic', 'mapEmbedUrl',
      'familleMarie', 'familleMariee', 'lieuReception', 'adresseTexte',
      'citation', 'referenceCitation', 'programme1', 'programme2',
      'posteMarie', 'posteMariee', 'phraseUnion', 'livreOrActive'
    ];
    
    simpleFields.forEach(field => {
      const value = formData[field as keyof FormDataState];
      if (value !== null && value !== undefined) {
        formDataToSend.append(field, value.toString());
      }
    });

    const dateAffichage = formatDateForDisplay(formData.dateISO);
    const heureAffichage = formatTimeForDisplay(formData.heureISO);
    
    formDataToSend.append('date', dateAffichage);
    formDataToSend.append('heure', heureAffichage);
    formDataToSend.append('theme', JSON.stringify(formData.theme));

    if (formData.photoBackground) {
      formDataToSend.append('photoBackground', formData.photoBackground);
    }
    if (formData.invitationImage) {
      formDataToSend.append('invitationImage', formData.invitationImage);
    }

    formDataToSend.append('boissonsSansAlcool', JSON.stringify(formData.boissonsSansAlcool));
    formDataToSend.append('boissonsAvecAlcool', JSON.stringify(formData.boissonsAvecAlcool));

    try {
      const response = await api.post<MariageResponse>("/mariages", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert(`Mariage créé avec succès !\nID: ${response.data.id}`);

      if (formData.photoBackgroundUrl) {
        URL.revokeObjectURL(formData.photoBackgroundUrl);
      }
      if (formData.invitationImageUrl) {
        URL.revokeObjectURL(formData.invitationImageUrl);
      }

      setFormData({
        template: "ElegantHero",
        bride: "",
        groom: "",
        province: "",
        photoBackground: null,
        invitationImage: null,
        jourSemaine: "",
        dateISO: "",
        heureISO: "",
        tableName: "",
        numeroMarie: "+243",
        numeroPicApic: "+243",
        mapEmbedUrl: "",
        theme: getDefaultTheme("ElegantHero"),
        boissonsSansAlcool: [],
        boissonsAvecAlcool: [],
        photoBackgroundUrl: "",
        invitationImageUrl: "",
        
        familleMarie: "",
        familleMariee: "",
        lieuReception: "",
        adresseTexte: "",
        citation: "",
        referenceCitation: "",
        programme1: "",
        programme2: "",
        
        posteMarie: "",
        posteMariee: "",
        phraseUnion: "",
        
        livreOrActive: true
      });

    } catch (err: any) {
      console.error("Erreur complète:", err);
      const errorMessage = err.response?.data?.message || err.message || "Erreur inconnue";
      alert(`Erreur : ${errorMessage}`);
    }
  };

  const renderThemeOption = (themeKey: string) => {
    const themeInfo = themeColors[themeKey];
    const isSelected = formData.template === themeKey;
    let symbol = "";
    
    if (themeInfo.family === "CLASSIQUE") symbol = "🏛️";
    else if (themeInfo.family === "MODERNE") symbol = "🏙️";
    else if (themeInfo.family === "ÉLÉGANT") symbol = "✨";
    else if (themeInfo.family === "TEMPLE") symbol = "🕍";
    
    return (
      <option key={themeKey} value={themeKey} style={{ 
        backgroundColor: isSelected ? '#e6f7ff' : 'white',
        fontWeight: isSelected ? 'bold' : 'normal',
        padding: '8px'
      }}>
        {`${symbol} [${themeInfo.family}] ${getThemeDisplayName(themeKey)}`}
        {` — ${themeInfo.colors[0]} - ${themeInfo.colors[1]}`}
      </option>
    );
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="mariage-form">
        <h1>Création d'une invitation de mariage</h1>
        <p className="form-subtitle">Remplissez ce formulaire pour générer une invitation personnalisée</p>

        <div className="form-section">
          <h2>Informations de base</h2>
          
          <div className="form-group">
            <label>Template *</label>
            <select 
              name="template" 
              value={formData.template} 
              onChange={handleChange} 
              required
              style={{ 
                padding: '10px',
                fontSize: '14px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                width: '100%'
              }}
            >
              <optgroup label="🏛️  FAMILLE CLASSIQUE" style={{ fontWeight: 'bold', color: '#8B4513' }}>
                {Object.keys(themeColors)
                  .filter(key => themeColors[key].family === "CLASSIQUE")
                  .map(key => renderThemeOption(key))}
              </optgroup>
              
              <optgroup label="🏙️  FAMILLE MODERNE" style={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                {Object.keys(themeColors)
                  .filter(key => themeColors[key].family === "MODERNE")
                  .map(key => renderThemeOption(key))}
              </optgroup>
              
              <optgroup label="✨  FAMILLE ÉLÉGANT" style={{ fontWeight: 'bold', color: '#800080' }}>
                {Object.keys(themeColors)
                  .filter(key => themeColors[key].family === "ÉLÉGANT")
                  .map(key => renderThemeOption(key))}
              </optgroup>

              <optgroup label="🕍  FAMILLE TEMPLE" style={{ fontWeight: 'bold', color: '#2a3a4a' }}>
                {Object.keys(themeColors)
                  .filter(key => themeColors[key].family === "TEMPLE")
                  .map(key => renderThemeOption(key))}
              </optgroup>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Prénom de la mariée *</label>
              <input 
                type="text" 
                name="bride" 
                placeholder="ex: Marie" 
                value={formData.bride} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="form-group">
              <label>Prénom du marié *</label>
              <input 
                type="text" 
                name="groom" 
                placeholder="ex: Jean" 
                value={formData.groom} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Nom de famille de la mariée *</label>
              <input 
                type="text" 
                name="posteMariee" 
                placeholder="ex: KAVIRA" 
                value={formData.posteMariee} 
                onChange={handleChange} 
                required 
              />
              <small className="hint">S'affichera en dessous du prénom de la mariée</small>
            </div>
            <div className="form-group">
              <label>Nom de famille du marié *</label>
              <input 
                type="text" 
                name="posteMarie" 
                placeholder="ex: KABONGO" 
                value={formData.posteMarie} 
                onChange={handleChange} 
                required 
              />
              <small className="hint">S'affichera en dessous du prénom du marié</small>
            </div>
          </div>

          <div className="form-group">
            <label>Province *</label>
            <select name="province" value={formData.province} onChange={handleChange} required>
              <option value="">Sélectionnez une province</option>
              {provinces.map(prov => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input 
                type="date" 
                name="dateISO" 
                value={formData.dateISO} 
                onChange={handleChange} 
                required 
              />
              {formData.dateISO && (
                <small className="hint">
                  Affichage: {formatDateForDisplay(formData.dateISO)}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Heure *</label>
              <input 
                type="time" 
                name="heureISO" 
                step="1"
                value={formData.heureISO} 
                onChange={handleChange} 
                required 
              />
              {formData.heureISO && (
                <small className="hint">
                  Affichage: {formatTimeForDisplay(formData.heureISO)}
                </small>
              )}
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input 
                type="checkbox" 
                name="livreOrActive" 
                checked={formData.livreOrActive} 
                onChange={handleChange} 
              />
              Activer le livre d'or
            </label>
            <small className="hint">Permet aux invités de laisser des messages dans le livre d'or</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Images</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Image de fond *</label>
              <input 
                type="file" 
                name="photoBackground" 
                accept="image/*" 
                onChange={handleFileChange} 
                required 
              />
              {formData.photoBackgroundUrl && (
                <div className="image-preview">
                  <img src={formData.photoBackgroundUrl} alt="Aperçu fond" />
                  <small>{formData.photoBackground?.name}</small>
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Image d'invitation</label>
              <input 
                type="file" 
                name="invitationImage" 
                accept="image/*" 
                onChange={handleFileChange} 
              />
              {formData.invitationImageUrl && (
                <div className="image-preview">
                  <img src={formData.invitationImageUrl} alt="Aperçu invitation" />
                  <small>{formData.invitationImage?.name}</small>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Aperçu du thème sélectionné</h2>
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            padding: '20px', 
            background: '#f5f5f5', 
            borderRadius: '8px',
            marginBottom: '20px',
            alignItems: 'center'
          }}>
            {themeColors[formData.template]?.colors.map((color, index) => (
              <div key={index} style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: color,
                borderRadius: '8px',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }} />
            ))}
            <div style={{ marginLeft: '10px' }}>
              <p style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>{getThemeDisplayName(formData.template)}</p>
              <p style={{ margin: '5px 0 0', color: '#666' }}>Famille: {themeColors[formData.template]?.family}</p>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Informations pour l'invitation</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Famille du marié</label>
              <input 
                type="text" 
                name="familleMarie" 
                placeholder="ex: MUPIRA" 
                value={formData.familleMarie} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label>Famille de la mariée</label>
              <input 
                type="text" 
                name="familleMariee" 
                placeholder="ex: TSHISWEKA" 
                value={formData.familleMariee} 
                onChange={handleChange} 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lieu de réception</label>
              <input 
                type="text" 
                name="lieuReception" 
                placeholder="ex: SALLE JUSTE IMAGINE" 
                value={formData.lieuReception} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label>Adresse (texte)</label>
              <input 
                type="text" 
                name="adresseTexte" 
                placeholder="ex: N° 19, AVENUE L'HAUT FA CHIN, GOLF" 
                value={formData.adresseTexte} 
                onChange={handleChange} 
              />
            </div>
          </div>

          <div className="form-group">
            <label>Citation</label>
            <textarea 
              name="citation" 
              placeholder="ex: « Celui qui trouve une femme trouve le bonheur... »" 
              value={formData.citation} 
              onChange={handleChange} 
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Référence citation</label>
              <input 
                type="text" 
                name="referenceCitation" 
                placeholder="ex: Proverbe 18:22" 
                value={formData.referenceCitation} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label>Programme 1</label>
              <input 
                type="text" 
                name="programme1" 
                placeholder="ex: MARIAGE CIVIL ET COUTUMIER" 
                value={formData.programme1} 
                onChange={handleChange} 
              />
            </div>
          </div>

          <div className="form-group">
            <label>Programme 2 (optionnel)</label>
            <input 
              type="text" 
              name="programme2" 
              placeholder="ex: CÉLÉBRATION RELIGIEUSE" 
              value={formData.programme2} 
              onChange={handleChange} 
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Personnalisation de l'image d'invitation</h2>
          
          <div className="form-group">
            <label>Phrase d'union personnalisée</label>
            <textarea 
              name="phraseUnion" 
              placeholder="ex: SOUS LE REGARD DE DIEU, ILS UNIRONT LEURS DESTINÉES..." 
              value={formData.phraseUnion} 
              onChange={handleChange} 
              rows={3}
            />
            <small className="hint">
              Laissez vide pour utiliser la phrase par défaut : "ENTOURÉS DE LEURS PROCHES, ILS SCELLERONT LEUR UNION DANS LA JOIE ET LE BONHEUR"
            </small>
          </div>
        </div>

        <div className="form-section">
          <h2>Couleurs du thème {getThemeDisplayName(formData.template)}</h2>
          
          <div className="colors-group">
            <h3>TEXTE PRINCIPAL</h3>
            <ColorInput 
              label="Couleur du texte principal"
              name="text"
              value={formData.theme.text}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>LIGNES SÉPARATION</h3>
            <ColorInput 
              label="Couleur des lignes de séparation"
              name="accentLine"
              value={formData.theme.accentLine}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>BOÎTE D'INVITATION</h3>
            <ColorInput 
              label="Fond de la boîte d'invitation"
              name="inviteBoxBackground"
              value={formData.theme.inviteBoxBackground}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>SECTIONS DE CONTENU</h3>
            <ColorInput 
              label="Fond des sections"
              name="sectionsBackground"
              value={formData.theme.sectionsBackground}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>BOUTONS & ÉLÉMENTS INTERACTIFS</h3>
            <ColorInput 
              label="Couleur des boutons (heart)"
              name="heart"
              value={formData.theme.heart}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>SECTION FINALE</h3>
            <ColorInput 
              label="Pied de page (accent)"
              name="accent"
              value={formData.theme.accent}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>FOND PRINCIPAL</h3>
            <ColorInput 
              label="Fond principal"
              name="background"
              value={formData.theme.background}
              onChange={handleThemeChange}
            />
          </div>

          <div className="colors-group">
            <h3>OVERLAY</h3>
            <ColorInput 
              label="Overlay sur les photos"
              name="overlay"
              value={formData.theme.overlay}
              onChange={handleThemeChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Boissons</h2>
          
          <div className="form-row">
            <div className="form-group">
              <h3>Sans alcool</h3>
              {formData.boissonsSansAlcool.map((boisson, index) => (
                <div key={index} className="input-with-remove">
                  <input
                    type="text"
                    placeholder="ex: Jus d'orange"
                    value={boisson}
                    onChange={(e) => handleBoissonChange("boissonsSansAlcool", index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-btn"
                    onClick={() => handleRemoveBoisson("boissonsSansAlcool", index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="add-btn"
                onClick={() => handleAddBoisson("boissonsSansAlcool")}
              >
                + Ajouter une boisson sans alcool
              </button>
            </div>
            
            <div className="form-group">
              <h3>Avec alcool</h3>
              {formData.boissonsAvecAlcool.map((boisson, index) => (
                <div key={index} className="input-with-remove">
                  <input
                    type="text"
                    placeholder="ex: Champagne"
                    value={boisson}
                    onChange={(e) => handleBoissonChange("boissonsAvecAlcool", index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-btn"
                    onClick={() => handleRemoveBoisson("boissonsAvecAlcool", index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="add-btn"
                onClick={() => handleAddBoisson("boissonsAvecAlcool")}
              >
                + Ajouter une boisson avec alcool
              </button>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Contact et localisation</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Numéro WhatsApp Marié *</label>
              <input 
                type="tel" 
                name="numeroMarie" 
                placeholder="ex: +243812345678" 
                value={formData.numeroMarie} 
                onChange={handleChange} 
                required 
              />
            </div>
            
            <div className="form-group">
              <label>Numéro WhatsApp PicApic *</label>
              <input 
                type="tel" 
                name="numeroPicApic" 
                placeholder="ex: +243812345678" 
                value={formData.numeroPicApic} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Google Maps Embed URL *</label>
            <textarea 
              name="mapEmbedUrl" 
              placeholder="Collez ici le code iframe de Google Maps" 
              value={formData.mapEmbedUrl} 
              onChange={handleChange} 
              rows={4}
              required 
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Créer l'invitation de mariage
          </button>
          <button 
            type="button" 
            className="reset-btn"
            onClick={() => {
              if (window.confirm("Êtes-vous sûr de vouloir réinitialiser le formulaire ?")) {
                if (formData.photoBackgroundUrl) {
                  URL.revokeObjectURL(formData.photoBackgroundUrl);
                }
                if (formData.invitationImageUrl) {
                  URL.revokeObjectURL(formData.invitationImageUrl);
                }
                
                setFormData({
                  template: "ElegantHero",
                  bride: "",
                  groom: "",
                  province: "",
                  photoBackground: null,
                  invitationImage: null,
                  jourSemaine: "",
                  dateISO: "",
                  heureISO: "",
                  tableName: "",
                  numeroMarie: "+243",
                  numeroPicApic: "+243",
                  mapEmbedUrl: "",
                  theme: getDefaultTheme("ElegantHero"),
                  boissonsSansAlcool: [],
                  boissonsAvecAlcool: [],
                  photoBackgroundUrl: "",
                  invitationImageUrl: "",
                  
                  familleMarie: "",
                  familleMariee: "",
                  lieuReception: "",
                  adresseTexte: "",
                  citation: "",
                  referenceCitation: "",
                  programme1: "",
                  programme2: "",
                  
                  posteMarie: "",
                  posteMariee: "",
                  phraseUnion: "",
                  
                  livreOrActive: true
                });
              }
            }}
          >
            Réinitialiser
          </button>
        </div>
        
        <div className="form-footer">
          <p><strong>* Champs obligatoires</strong></p>
        </div>
      </form>
    </div>
  );
};

export default MariageForm;